// Backups: building one (everything on the phone) and restoring one. Used by the backup file under More and by
// the cloud backup (cloud.ts). Restoring merges: what is in the backup is added or updated, nothing is removed.
import type { Catch, Group, GroupMessage, Pin, Route, Trip } from "../shared/model.ts";
import { dbAll, dbPut } from "./db.ts";
import { $, saveFile } from "./dom.ts";
import { fmtNum, t } from "./i18n.ts";
import { blobToDataUrl, dataUrlToBlob } from "./photos.ts";
import { upgrade } from "./species.ts";
import { catches, render, setCatches } from "./state.ts";
import { lsJson } from "./util.ts";
import { refreshWeather } from "./weather.ts";
import { loadPlaces } from "./map/places.ts";
import { loadGroups } from "./groups/store.ts";
import { saveProfile, type Profile } from "./tips.ts";
import { markBackedUp, takeSnapshot } from "./safety.ts";

/** The backup's contents (version 1). Photos are data URLs; a cloud backup has only the small ones (`thumb`). */
export interface BackupFile {
  app: "hunt-fish-log";
  version: 1;
  exportedAt: string;
  photoSize?: "full" | "thumb";
  catches: Catch[];
  photos: { id: string; full?: string; thumb: string }[];
  pins?: Pin[];
  routes?: Route[];
  groups?: Group[];
  messages?: GroupMessage[];
  trips?: Trip[];
  profile?: Profile | null;
}

/** Everything on the phone as a backup. `photoSize: "thumb"` leaves out the full-size photos (much smaller). */
export async function buildBackup(photoSize: "full" | "thumb"): Promise<BackupFile> {
  const photos: BackupFile["photos"] = [];
  for (const p of await dbAll("photos")) {
    photos.push(photoSize === "full" ? { id: p.id, full: await blobToDataUrl(p.full), thumb: await blobToDataUrl(p.thumb) } : { id: p.id, thumb: await blobToDataUrl(p.thumb) });
  }
  return { app: "hunt-fish-log", version: 1, exportedAt: new Date().toISOString(), photoSize, catches: await dbAll("catches"), photos,
    pins: await dbAll("pins"), routes: await dbAll("routes"), groups: await dbAll("groups"), messages: await dbAll("messages"),
    trips: await dbAll("trips"), profile: lsJson<Profile | null>("profile", null) };
}

/** Adds and updates everything in the backup (keeps what is only on the phone). Returns how many catches it had. */
export async function restoreBackup(data: BackupFile): Promise<number> {
  if (data.app !== "hunt-fish-log" || !Array.isArray(data.catches)) throw new Error("not a backup");
  await takeSnapshot("restore").catch(() => {}); // a copy first, in case
  const have = new Set((await dbAll("photos")).map((p) => p.id));
  for (const p of data.photos ?? []) {
    // a cloud backup's small photo doesn't replace a full-size one already on the phone
    if (!p.full && have.has(p.id)) continue;
    const thumb = await dataUrlToBlob(p.thumb);
    await dbPut("photos", { id: p.id, full: p.full ? await dataUrlToBlob(p.full) : thumb, thumb });
  }
  for (const c of data.catches) await dbPut("catches", c);
  for (const p of data.pins ?? []) await dbPut("pins", p);
  for (const r of data.routes ?? []) await dbPut("routes", r);
  for (const g of data.groups ?? []) await dbPut("groups", g);
  for (const m of data.messages ?? []) await dbPut("messages", m);
  for (const tr of data.trips ?? []) if (tr.endedAt) await dbPut("trips", tr); // a trip still running elsewhere isn't brought over
  if (data.profile) saveProfile(data.profile);
  await loadGroups();
  await loadPlaces();
  setCatches((await dbAll("catches")).map(upgrade));
  render();
  void refreshWeather();
  return data.catches.length;
}

/* ---------- the backup file under More ---------- */
function setStatus(msg: string, err = false): void {
  const s = $("#backup-status");
  s.textContent = msg;
  s.classList.toggle("err", err);
}

$("#export").addEventListener("click", async () => {
  setStatus(t("backupPreparing"));
  try {
    const data = await buildBackup("full");
    const name = `reelhunt-backup-${new Date().toISOString().slice(0, 10)}.json`;
    const how = await saveFile(new Blob([JSON.stringify(data)], { type: "application/json" }), name, t("backupShareTitle"));
    if (how === "cancelled") { setStatus(""); return; }
    const pins = data.pins?.length ?? 0;
    setStatus(how === "shared" ? t("backupShared", data.catches.length, pins) : t("backupSaved", name, data.catches.length, pins));
    markBackedUp();
  } catch (e) { console.error(e); setStatus(t("backupFailed"), true); }
});

$("#import-file").addEventListener("change", async (e) => {
  const fileInput = e.currentTarget as HTMLInputElement;
  const f = fileInput.files?.[0];
  fileInput.value = "";
  if (!f) return;
  setStatus(t("restoring"));
  try { setStatus(t("restored", await restoreBackup(JSON.parse(await f.text()) as BackupFile))); }
  catch (err) { console.error(err); setStatus(t("restoreBad"), true); }
});

/** "12 catches and 30 photos stored, using about 18 MB." */
export async function storageInfo(): Promise<void> {
  const p = $("#storage-info");
  try {
    const est = await navigator.storage?.estimate?.();
    const photos = catches.reduce((s, c) => s + (c.photoIds?.length ?? 0), 0);
    p.textContent = t("storageInfo", catches.length, photos, est?.usage ? fmtNum(est.usage / 1048576, 1) : null);
  } catch { p.textContent = ""; }
}
